import moment from "moment";
import isNil from "lodash/isNil";
import range from "lodash/range";
import memoize from "lodash/memoize";
import { Map, List } from "immutable";

import Mapper from "infrastructure/mapper";
import { EMPTY_MAP } from "app/app.constants";
import {
  shiftTypeToString,
  QualificationMapper,
  DeploymentDetailMapper,
  CardMapper,
  WorkScheduleOverrideMapper,
} from "features/planner/planner.mappers";
import { BOOKING_SYSTEM_TYPES } from "features/planner/planner.constants";
import { WORK_SCHEDULE_REPORT_TYPE } from "features/planner/report/report.constants";

export const POBReportEmployeeMapper = new Mapper({
  from: ({
    employeeId,
    firstName,
    lastName,
    travelSysDaWinci,
    travelSysTicket,
    travelSysVantage,
    ...pobReportEmployee
  }) =>
    Map({
      id: employeeId,
      displayName: `${firstName} ${lastName}`,
      travel: {
        [BOOKING_SYSTEM_TYPES.TICKET]: travelSysTicket,
        [BOOKING_SYSTEM_TYPES.VANTAGE]: travelSysVantage,
        [BOOKING_SYSTEM_TYPES.DA_WINCHI]: travelSysDaWinci,
      },
      ...pobReportEmployee,
    }),
});

export const POBReportMapper = new Mapper({
  from: ({ employees, name, ...pobReport }) =>
    Map({
      groupName: name,
      employees: List(employees).map(POBReportEmployeeMapper.from),
      ...pobReport,
    }),
});

export const HeliReportEmployeeMapper = new Mapper({
  initial: ({ index }) =>
    Map({
      index,
    }),
  from: ({ employeeId, ...heliReportEmployee }) =>
    Map({
      id: employeeId,
      ...heliReportEmployee,
    }),
  to: (heliEmployee, index) => {
    if (!heliEmployee) return heliEmployee;

    const { shiftType } = heliEmployee;

    return {
      ...heliEmployee,
      index,
      shiftType: isNil(shiftType) ? null : shiftTypeToString(shiftType)[0],
    };
  },
});

export const HeliReportMapper = new Mapper({
  initial: () => EMPTY_MAP,
  from: ({
    offshoreBound,
    shoreBound,
    responsible,
    responsibleEmail,
    responsiblePhone,
    bookingSystem,
    maxIndex,
    ...heliReport
  }) => {
    const shoreBoundByIndex = Map(
      shoreBound.map(shoreBound => [shoreBound.index, shoreBound]),
    );

    const offshoreBoundByIndex = Map(
      offshoreBound.map(offshoreBound => [offshoreBound.index, offshoreBound]),
    );

    return Map({
      maxIndex,
      bookingSystemType: bookingSystem,
      responsible: Map({
        name: responsible,
        email: responsibleEmail,
        phone: responsiblePhone,
      }),
      shoreBound: List(range(maxIndex + 1)).map(index =>
        shoreBoundByIndex.get(index)
          ? HeliReportEmployeeMapper.from(shoreBoundByIndex.get(index))
          : HeliReportEmployeeMapper.initial(EMPTY_MAP),
      ),
      offshoreBound: List(range(maxIndex + 1)).map(index =>
        offshoreBoundByIndex.get(index)
          ? HeliReportEmployeeMapper.from(offshoreBoundByIndex.get(index))
          : HeliReportEmployeeMapper.initial(EMPTY_MAP),
      ),
      ...heliReport,
    });
  },
  to: ({ shoreBound, offshoreBound, responsible, ...heliReport }) => ({
    shoreBound: shoreBound.map(HeliReportEmployeeMapper.to),
    offshoreBound: offshoreBound.map(HeliReportEmployeeMapper.to),
    responsible: responsible.name,
    responsibleEmail: responsible.email,
    responsiblePhone: responsible.phone,
    ...heliReport,
  }),
});

export const workScheduleReportTypeToString = memoize(
  reportType =>
    ({
      [WORK_SCHEDULE_REPORT_TYPE.WEEK]: "Month",
      [WORK_SCHEDULE_REPORT_TYPE.MONTH]: "Month",
      [WORK_SCHEDULE_REPORT_TYPE.YEAR]: "Year",
    }[reportType]),
);

export const EmployeesByGroupMapper = new Mapper({
  from: ({ employeeGroup, employees, ...employeesByGroup }) =>
    Map({
      employees: employees
        ? List(employees).map(WorkScheduleReportEmployeeMapper.from)
        : employees,
      ...employeesByGroup,
    }),
});

export const WorkScheduleReportMapper = new Mapper({
  from: ({ reportParameters, employeesByGroup, ...workSheduleReport }) =>
    Map({
      employeesByGroup: List(employeesByGroup).map(EmployeesByGroupMapper.from),
      parameters: WorkScheduleReportParametersMapper.from(reportParameters),
      ...workSheduleReport,
    }),
});

export const WorkScheduleReportEmployeeMapper = new Mapper({
  from: ({
    qualification,
    scheduleDays,
    scheduleWeeks,
    deploymentDetails,
    scheduleOverrides,
    card,
    ...workScheduleReportEmployee
  }) =>
    Map({
      qualification: qualification
        ? QualificationMapper.from(qualification)
        : qualification,
      scheduleDays: List(scheduleDays).map(
        WorkScheduleReportEmployeeScheduleDayMapper.from,
      ),
      scheduleWeeks: List(scheduleWeeks).map(
        WorkScheduleReportEmployeeScheduleWeekMapper.from,
      ),
      deploymentDetails: List(deploymentDetails).map(
        DeploymentDetailMapper.from,
      ),
      scheduleOverrides: List(scheduleOverrides).map(
        WorkScheduleOverrideMapper.from,
      ),
      card: card ? CardMapper.from(card) : card,
      ...workScheduleReportEmployee,
    }),
});

export const WorkScheduleReportEmployeeScheduleDayMapper = new Mapper({
  from: ({ date, ...workScheduleReportEmployeeScheduleDay }) =>
    Map({
      date: moment(date),
      ...workScheduleReportEmployeeScheduleDay,
    }),
});

export const WorkScheduleReportEmployeeScheduleWeekMapper = new Mapper({
  from: ({
    weekStartDate,
    scheduleDays,
    ...workScheduleReportEmployeeScheduleWeek
  }) =>
    Map({
      scheduleDays: scheduleDays
        ? scheduleDays.map(WorkScheduleReportEmployeeScheduleDayMapper.from)
        : scheduleDays,
      weekStartDate: moment(weekStartDate),
      ...workScheduleReportEmployeeScheduleWeek,
    }),
});

export const WorkScheduleReportParametersMapper = new Mapper({
  from: ({ startDate, endDate, ...workScheduleReportParameters }) =>
    Map({
      startDate: moment(startDate),
      endDate: moment(endDate),
      ...workScheduleReportParameters,
    }),
  to: ({ startDate, endDate, month, ...workScheduleReportParameters }) => ({
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    month: month + 1,
    ...workScheduleReportParameters,
  }),
});
